<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router';
import { useToast } from 'primevue/usetoast';
import type { CrewBriefing, CrewBriefingUpsertRequest } from '@/api/client/models/index.ts';
import CrewBriefingDownloadDialog from '@/charter/components/CrewBriefingDownloadDialog.vue';
import CrewBriefingRichTextEditor from '@/charter/components/CrewBriefingRichTextEditor.vue';
import { useCharterCrewBriefingDetailStore } from '@/charter/stores/charter-crew-briefing-detail-store.ts';
import { getApiErrorMessages, getApiErrorStatus } from '@/util/error-utils.ts';

type CrewBriefingField =
  | 'flightInfo'
  | 'cabinLayout'
  | 'cateringInfo'
  | 'cleaningInfo'
  | 'specials'
  | 'crewTransport'
  | 'hotacInfo'
  | 'announcements'
  | 'codeOfConduct'
  | 'loading';

type CrewBriefingForm = Record<CrewBriefingField, string>;

const fields: ReadonlyArray<{ key: CrewBriefingField; label: string }> = [
  { key: 'flightInfo', label: 'Flight info' },
  { key: 'cabinLayout', label: 'Cabin layout' },
  { key: 'cateringInfo', label: 'Catering info' },
  { key: 'cleaningInfo', label: 'Cleaning info' },
  { key: 'specials', label: 'Specials' },
  { key: 'crewTransport', label: 'Crew transport' },
  { key: 'hotacInfo', label: 'HOTAC info' },
  { key: 'announcements', label: 'Announcements' },
  { key: 'codeOfConduct', label: 'Code of conduct' },
  { key: 'loading', label: 'Loading' },
];

const route = useRoute();
const router = useRouter();
const toast = useToast();
const detailStore = useCharterCrewBriefingDetailStore();

const form = reactive<CrewBriefingForm>(createEmptyForm());
const version = ref<number | null>(null);
const savedForm = ref(serializeForm(form));
const formReady = ref(false);
const loadError = ref<string | null>(null);
const saveError = ref<string | null>(null);
const hasConflict = ref(false);
const downloadDialogVisible = ref(false);

const charterId = computed(() => parseCharterId(route.params.charterId));
const isValidCharterId = computed(() => charterId.value !== null);
const isDirty = computed(() => formReady.value && serializeForm(form) !== savedForm.value);
const isBusy = computed(() => detailStore.isLoading || detailStore.isSaving);
const downloadDisabledReason = computed(() => {
  if (isDirty.value) {
    return 'Save your changes before downloading.';
  }
  if (detailStore.isLoading || detailStore.isSaving) {
    return 'Wait for the crew briefing operation to finish.';
  }
  if (detailStore.isDownloading) {
    return 'A crew briefing download is already in progress.';
  }
  return null;
});
const isDownloadDisabled = computed(() => downloadDisabledReason.value !== null);

function createEmptyForm(): CrewBriefingForm {
  return {
    flightInfo: '',
    cabinLayout: '',
    cateringInfo: '',
    cleaningInfo: '',
    specials: '',
    crewTransport: '',
    hotacInfo: '',
    announcements: '',
    codeOfConduct: '',
    loading: '',
  };
}

function parseCharterId(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isSafeInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

function serializeForm(value: CrewBriefingForm): string {
  return JSON.stringify(value);
}

function applyBriefing(briefing: CrewBriefing | null): void {
  fields.forEach(({ key }) => {
    form[key] = briefing?.[key] ?? '';
  });
  version.value = briefing?.version ?? null;
  savedForm.value = serializeForm(form);
  formReady.value = true;
}

function resetPage(): void {
  Object.assign(form, createEmptyForm());
  version.value = null;
  savedForm.value = serializeForm(form);
  formReady.value = false;
  loadError.value = null;
  saveError.value = null;
  hasConflict.value = false;
  downloadDialogVisible.value = false;
  detailStore.reset();
}

async function loadCrewBriefing(): Promise<void> {
  resetPage();
  const id = charterId.value;
  if (id === null) {
    return;
  }

  try {
    applyBriefing(await detailStore.load(id));
  } catch (error) {
    loadError.value = getApiErrorMessages(error, 'Crew briefing could not be loaded.').join(' ');
  }
}

function toNullableValue(value: string): string | null {
  return value === '' ? null : value;
}

function createSaveRequest(): CrewBriefingUpsertRequest {
  const request: CrewBriefingUpsertRequest = {
    flightInfo: toNullableValue(form.flightInfo),
    cabinLayout: toNullableValue(form.cabinLayout),
    cateringInfo: toNullableValue(form.cateringInfo),
    cleaningInfo: toNullableValue(form.cleaningInfo),
    specials: toNullableValue(form.specials),
    crewTransport: toNullableValue(form.crewTransport),
    hotacInfo: toNullableValue(form.hotacInfo),
    announcements: toNullableValue(form.announcements),
    codeOfConduct: toNullableValue(form.codeOfConduct),
    loading: toNullableValue(form.loading),
  };

  if (version.value !== null) {
    request.version = version.value;
  }

  return request;
}

async function saveCrewBriefing(): Promise<void> {
  const id = charterId.value;
  if (id === null || !formReady.value || !isDirty.value) {
    return;
  }

  saveError.value = null;
  hasConflict.value = false;

  try {
    const savedBriefing = await detailStore.save(id, createSaveRequest());
    applyBriefing(savedBriefing);
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Crew briefing saved successfully',
      life: 3000,
    });
  } catch (error) {
    hasConflict.value = getApiErrorStatus(error) === 409;
    saveError.value = hasConflict.value
      ? 'This crew briefing was changed by another user. Reload the latest version before saving again.'
      : getApiErrorMessages(error, 'Crew briefing could not be saved.').join(' ');
  }
}

async function reloadLatest(): Promise<void> {
  if (isDirty.value && !window.confirm('Reload the latest crew briefing and discard your unsaved changes?')) {
    return;
  }

  await loadCrewBriefing();
}

function goBack(): void {
  router.back();
}

function openDownloadDialog(): void {
  if (!isDownloadDisabled.value) {
    downloadDialogVisible.value = true;
  }
}

function confirmDiscardChanges(): boolean {
  return !isDirty.value || window.confirm('You have unsaved crew briefing changes. Leave this page?');
}

function handleBeforeUnload(event: BeforeUnloadEvent): void {
  if (!isDirty.value) {
    return;
  }

  event.preventDefault();
  event.returnValue = true;
}

function confirmRouteUpdate(): boolean {
  return confirmDiscardChanges();
}

onBeforeRouteLeave(confirmDiscardChanges);
onBeforeRouteUpdate(confirmRouteUpdate);

watch(
  () => route.params.charterId,
  () => {
    void loadCrewBriefing();
  }
);

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
  void loadCrewBriefing();
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
  detailStore.reset();
});
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="m-0">Crew briefing</h1>
        <p v-if="isValidCharterId" class="mb-0 mt-1 text-slate-500">Charter {{ charterId }}</p>
      </div>
      <div class="flex gap-2">
        <p-button
          label="Back"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          :disabled="isBusy"
          @click="goBack"
        />
        <span
          v-if="formReady"
          class="inline-flex"
          :title="downloadDisabledReason ?? undefined"
        >
          <p-button
            label="Download"
            icon="pi pi-download"
            severity="secondary"
            outlined
            :loading="detailStore.isDownloading"
            :disabled="isDownloadDisabled"
            :aria-label="
              downloadDisabledReason
                ? `Download crew briefing. ${downloadDisabledReason}`
                : 'Download crew briefing'
            "
            @click="openDownloadDialog"
          />
        </span>
        <p-button
          v-if="formReady"
          label="Save"
          icon="pi pi-save"
          type="submit"
          form="crew-briefing-form"
          :loading="detailStore.isSaving"
          :disabled="detailStore.isLoading || detailStore.isSaving || !isDirty"
        />
      </div>
    </div>

    <div
      v-if="!isValidCharterId"
      class="rounded-md border-l-4 border-red-500 bg-red-50 p-4 text-red-700"
      role="alert"
    >
      This crew briefing is unavailable because the charter identifier is invalid.
    </div>

    <div v-else-if="detailStore.isLoading && !formReady" class="flex flex-1 items-center justify-center">
      <p-progress-spinner animationDuration=".8s" />
    </div>

    <div
      v-else-if="loadError && !formReady"
      class="rounded-md border-l-4 border-red-500 bg-red-50 p-4 text-red-700"
      role="alert"
    >
      <p class="m-0">{{ loadError }}</p>
      <p-button
        class="mt-3"
        label="Retry"
        icon="pi pi-refresh"
        severity="danger"
        outlined
        :loading="detailStore.isLoading"
        @click="loadCrewBriefing"
      />
    </div>

    <form
      v-else-if="formReady"
      id="crew-briefing-form"
      class="flex flex-col gap-4"
      @submit.prevent="saveCrewBriefing"
    >
      <div
        v-if="loadError"
        class="rounded-md border-l-4 border-red-500 bg-red-50 p-3 text-red-700"
        role="alert"
      >
        {{ loadError }}
      </div>

      <div
        v-if="saveError"
        class="flex flex-wrap items-center justify-between gap-3 rounded-md border-l-4 border-red-500 bg-red-50 p-3 text-red-700"
        role="alert"
      >
        <span>{{ saveError }}</span>
        <p-button
          v-if="hasConflict"
          label="Reload latest"
          icon="pi pi-refresh"
          severity="danger"
          outlined
          :loading="detailStore.isLoading"
          @click="reloadLatest"
        />
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div v-for="field in fields" :key="field.key" class="space-y-2">
          <label
            :id="`${field.key}-label`"
            :for="field.key"
            class="block font-medium text-slate-700"
          >
            {{ field.label }}
          </label>
          <CrewBriefingRichTextEditor
            :id="field.key"
            v-model="form[field.key]"
            :labelledBy="`${field.key}-label`"
            :disabled="isBusy"
          />
        </div>
      </div>

    </form>

    <CrewBriefingDownloadDialog
      v-if="charterId !== null"
      v-model:visible="downloadDialogVisible"
      :charterId="charterId"
    />
  </div>
</template>
